import {useContext} from 'react'

import PropTypes from 'prop-types'

import {parseQueryString} from '@s-ui/js/lib/string'
import Context from '@s-ui/react-context'
import Helmet from '@s-ui/react-head'
import {useMount} from '@s-ui/react-hooks'
import AdDisplay from '@adv-mt/mt-ad-display'
import EmptyCase from '@adv-mt/mt-empty-case-default'
import Layout from '@adv-mt/mt-layout-basic'
import {useMediaQuery, useMediaQueryBreakpoints} from '@adv-mt/mt-layout-render-media-query'
import ListPagination from '@adv-mt/mt-list-pagination'
import ListVersions from '@adv-mt/mt-list-versions'
import Breadcrumb from '@adv-mt/mt-navigation-breadcrumb'
import SearchHeader from '@adv-mt/mt-search-header'
import SearchSidebar, {searchSidebarPlaces} from '@adv-mt/mt-search-sidebar'
import SearchSort, {searchSortButtonDesigns} from '@adv-mt/mt-search-sort'
import TitleBasic, {titleBasicSizes, titleBasicTags, titleBasicTextAlign} from '@adv-mt/mt-title-basic'
import MotorTrackingCriteo from '@adv-mt/mt-tracking-criteo'
import TrackingPageView from '@adv-mt/mt-tracking-page-view'

import LinkFactory from '../../../components/LinkFactory/index.js'
import {getTrackingData} from './services/trackingDataService.js'
import {getInitialProps} from './getInitialProps.js'

const BASE_CLASS = 'mt-TechnicalDetailsSearch'
const INNER_CLASS = `${BASE_CLASS}-inner`
const SIDEBAR_CLASS = `${INNER_CLASS}Sidebar`
const CONTENT_CLASS = `${INNER_CLASS}Content`
const EMPTY_CLASS = `${INNER_CLASS}Empty`

const emptyCaseProps = {
  text: 'EMPTY_CASE_TECHNICAL_SEARCH_NO_RESULTS_TEXT',
  title: 'EMPTY_CASE_NO_RESULTS_TITLE',
  imageUrl: 'images/empty-case-technical-search.svg',
  imageSize: 'medium'
}

const TechnicalDetailsSearch = ({
  breadcrumb,
  helmet,
  initialResultsResponse,
  lastFilter,
  loadThirdParties,
  pageName,
  paginationUrl,
  router,
  search
}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const {TECHNICAL_DETAILS_VERSIONS_TABLE_CONFIG: tableColumnsConfig} = config.get('technicalDetails')
  const isMobile = config.get('isMobile')
  const isTablet = config.get('isTablet')
  const isDesktopOrWider = useMediaQuery(useMediaQueryBreakpoints.lg, {
    defaultMatches: !isMobile && !isTablet
  })

  const handleRedirectTo = url => {
    router.push(url)
  }

  useMount(() => {
    const _searchUpdate$ = domain.get('get_search_url_vehicles_use_case').$.execute.subscribe(({result}) => {
      const {lastFilter} = result
      const query = parseQueryString(result.url.split('?')[1])

      handleRedirectTo({
        pathname: router.location.pathname,
        query,
        state: {lastFilter}
      })
    })
    return () => _searchUpdate$.dispose()
  })

  const {items: vehicles} = initialResultsResponse || {}
  const {appliedFilters: {make, model, modelVersion, fuelType} = {}} = initialResultsResponse || {}
  const {appliedFilters} = initialResultsResponse || {}
  const {meta: {totalResults} = {}} = initialResultsResponse || {}

  const handleClickVehicle = index => {
    const {url} = vehicles[index]
    handleRedirectTo(url)
  }

  const trackingData = getTrackingData({
    search,
    appliedFilters,
    lastFilter,
    totalResults
  })

  const SEARCH_RESULTS_TITLE =
    make || model || modelVersion || fuelType
      ? i18n.t('TECHNICAL_DETAILS_SEARCH_RESULTS_TITLE', {
          makeName: make || '',
          modelName: model || modelVersion || '',
          fuelType: fuelType || ''
        })
      : i18n.t('COMMON.PLURALS.TECHNICAL_DETAIL', totalResults)

  const title = `${totalResults} ${SEARCH_RESULTS_TITLE}`

  return (
    <>
      <Helmet {...helmet} />
      {!isDesktopOrWider && (
        <div className={`${BASE_CLASS}-searchTopbar`}>
          <SearchHeader search={search} redirectTo={handleRedirectTo} pageName={pageName} totalResults={totalResults} />
        </div>
      )}
      <Layout.Basic linkFactory={LinkFactory} isMarginless>
        <div className={`${BASE_CLASS}-titleHeader`}>
          <div className={`${BASE_CLASS}-title`}>
            <TitleBasic
              as={titleBasicTags.h1}
              size={isDesktopOrWider ? titleBasicSizes.xs : titleBasicSizes.xxs}
              textAlign={isDesktopOrWider ? titleBasicTextAlign.left : titleBasicTextAlign.center}
              title={title}
            />
          </div>
          <SearchSort
            design={searchSortButtonDesigns.BASIC}
            linkFactory={LinkFactory}
            pageName={pageName}
            search={search}
            withSortOrderInfo
          />
        </div>
        <div className={BASE_CLASS}>
          <div className={INNER_CLASS}>
            <div className={SIDEBAR_CLASS}>
              <SearchSidebar
                pageName={pageName}
                place={searchSidebarPlaces.TECHNICAL_DETAILS}
                redirectTo={handleRedirectTo}
                search={search}
              />
            </div>
            {vehicles.length ? (
              <div className={CONTENT_CLASS}>
                <ListVersions
                  tableColumnsConfig={tableColumnsConfig}
                  vehicles={vehicles}
                  onRowClick={handleClickVehicle}
                />
                <ListPagination
                  currentPage={search.page}
                  totalPages={initialResultsResponse.meta.totalPages}
                  linkFactory={LinkFactory}
                  urlPattern={paginationUrl}
                />
              </div>
            ) : (
              <div className={EMPTY_CLASS}>
                <EmptyCase {...emptyCaseProps} />
              </div>
            )}
          </div>
          {breadcrumb && <Breadcrumb items={breadcrumb} linkFactory={LinkFactory} />}
        </div>
        {loadThirdParties && (
          <>
            <TrackingPageView pageName={pageName} searchResultsCount={totalResults} {...trackingData} />
            <AdDisplay pageName={pageName} />
            <MotorTrackingCriteo />
          </>
        )}
      </Layout.Basic>
    </>
  )
}

TechnicalDetailsSearch.displayName = 'TechnicalDetailsSearch'

TechnicalDetailsSearch.propTypes = {
  breadcrumb: PropTypes.array,
  helmet: PropTypes.object,
  lastFilter: PropTypes.string,
  loadThirdParties: PropTypes.bool,
  pageName: PropTypes.string,
  paginationUrl: PropTypes.string,
  router: PropTypes.object,
  search: PropTypes.object,
  initialResultsResponse: PropTypes.shape({
    appliedFilters: PropTypes.object,
    items: PropTypes.array,
    meta: PropTypes.object
  })
}

TechnicalDetailsSearch.getInitialProps = getInitialProps

export default TechnicalDetailsSearch
